import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type MutableRefObject } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { ContactShadows, Html, OrbitControls } from '@react-three/drei';
import type { OrbitControls as OrbitControlsImpl } from 'three-stdlib';
import * as THREE from 'three';
import {
  advanceCupPhysics,
  FAN_BALL_LIMIT,
  getCupPhysicsRuntime,
  getExistingCupPhysicsRuntime,
  INTRO_BOX_COUNT,
  stadiumPlayerRenderPose,
  spawnFanBall,
  STADIUM_PLAYER_LAYOUT,
  type CupPhysicsRuntime,
} from '../physics/cupPhysics';
import {
  LAST_UPDATED,
  MATCHES,
  TEAMS,
  type Match,
  type Stage,
  type Team,
} from '../data/worldcup2026';
import './WorldCup.css';

type CupMode = 'stadium' | 'bracket' | 'shootout';
type ShotResult = 'goal' | 'saved' | 'post';
type ShootoutPhase = 'pick' | 'play' | 'ended';
type SaveZone = 'left-high' | 'center-high' | 'right-high' | 'left-low' | 'center-low' | 'right-low';

interface CupState {
  mode: CupMode;
  dataLoaded: boolean;
  matchCount: number;
  canvasReady: boolean;
  physicsReady: boolean;
  fanBallCount: number;
}

interface AimPoint {
  x: number;
  y: number;
}

interface ShootoutEvent {
  token: number;
  target: AimPoint;
  keeperZone: SaveZone;
  outcome: ShotResult;
}

interface ShootoutRecord {
  bestScore: number;
  gamesPlayed: number;
  winStreak: number;
}

interface CupDebugState {
  fixedTime: number;
  ambientReady: boolean;
  introPhase: 'assembling' | 'holding' | 'collapsing' | 'done';
  ball: { x: number; y: number; z: number };
  introBlocks: Array<{ x: number; y: number; z: number }>;
  introTargets: Array<{ x: number; y: number; z: number }>;
  kick: {
    active: boolean;
    playerIndex: number;
    phase: 'idle' | 'lunge' | 'strike' | 'recover';
    nearestPlayer: { x: number; y: number; z: number };
    ballAtStrike: { x: number; y: number; z: number } | null;
  };
}

declare global {
  interface Window {
    __cupState?: CupState;
  }
}

function writeCupState(patch: Partial<CupState>) {
  const previous = window.__cupState;
  window.__cupState = {
    mode: patch.mode ?? previous?.mode ?? 'stadium',
    dataLoaded: patch.dataLoaded ?? previous?.dataLoaded ?? true,
    matchCount: patch.matchCount ?? previous?.matchCount ?? MATCHES.length,
    canvasReady: patch.canvasReady ?? previous?.canvasReady ?? false,
    physicsReady: patch.physicsReady ?? previous?.physicsReady ?? false,
    fanBallCount: patch.fanBallCount ?? previous?.fanBallCount ?? 0,
  };
}

const STAGES: Stage[] = ['R32', 'R16', 'QF', 'SF', 'F'];
const STAGE_LABEL: Record<Stage, string> = {
  R32: 'Round of 32',
  R16: 'Round of 16',
  QF: 'Quarterfinal',
  SF: 'Semifinal',
  F3: 'Third Place',
  F: 'Final',
};
const BRACKET_STAGE_LABEL: Record<Stage, string> = {
  R32: 'Round of 32',
  R16: 'Round of 16',
  QF: 'Quarter Finals',
  SF: 'Semi Finals',
  F3: 'Third Place',
  F: 'Final',
};
const BRACKET_X: Record<Stage, number> = { R32: -15, R16: -7.5, QF: 0, SF: 7.5, F: 15, F3: 15 };

const DEFAULT_TEAMS: Team[] = [
  { code: 'C26', name: 'Cup XI', flagEmoji: '26', kit: { primary: '#20d878', secondary: '#10213a' } },
  { code: 'FAN', name: 'Fan XI', flagEmoji: 'FC', kit: { primary: '#32c8ff', secondary: '#ffcf5a' } },
];

const FAN_COLORS = ['#22f08f', '#3fdcff', '#ffd957', '#ff6685', '#fff8df', '#4f7dff', '#ff9a3d'];
const RECORD_KEY = 'cup26.shootout';

function orderedTeams(): Team[] {
  const teams = Object.values(TEAMS);
  return teams.length > 0 ? teams : DEFAULT_TEAMS;
}

function getTeam(code: string | null): Team | null {
  return code ? TEAMS[code] ?? null : null;
}

function fallbackPair(): [Team, Team] {
  const teams = orderedTeams();
  return [teams[0] ?? DEFAULT_TEAMS[0], teams[1] ?? DEFAULT_TEAMS[1]];
}

function kickoffTime(match: Match): number {
  const t = Date.parse(match.kickoffUtc);
  return Number.isFinite(t) ? t : 0;
}

function localDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = `${date.getMonth() + 1}`.padStart(2, '0');
  const d = `${date.getDate()}`.padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatLocalTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'TBD';
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function formatShortDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'TBD';
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function formatUpdated(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
}

function countdownParts(nowMs: number, kickoffIso: string): Array<{ label: string; value: string }> {
  const target = Date.parse(kickoffIso);
  if (!Number.isFinite(target)) {
    return [
      { label: 'Days', value: '00' },
      { label: 'Hours', value: '00' },
      { label: 'Minutes', value: '00' },
      { label: 'Seconds', value: '00' },
    ];
  }
  const diff = Math.max(0, target - nowMs);
  const total = Math.floor(diff / 1000);
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const mins = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  return [
    { label: 'Days', value: days.toString().padStart(2, '0') },
    { label: 'Hours', value: hours.toString().padStart(2, '0') },
    { label: 'Minutes', value: mins.toString().padStart(2, '0') },
    { label: 'Seconds', value: secs.toString().padStart(2, '0') },
  ];
}

function scoreText(match: Match): string {
  if (match.homeScore == null || match.awayScore == null) {
    return match.status === 'live' ? 'LIVE' : formatShortDate(match.kickoffUtc);
  }
  const pens = match.penalties ? ` pens ${match.penalties.home}-${match.penalties.away}` : '';
  return `${match.homeScore}-${match.awayScore}${pens}`;
}

function matchName(match: Match): string {
  const home = getTeam(match.home);
  const away = getTeam(match.away);
  return `${home?.code ?? 'TBD'} vs ${away?.code ?? 'TBD'}`;
}

function kitVars(team: Team | null): CSSProperties {
  return {
    '--kit-primary': team?.kit.primary ?? '#64748b',
    '--kit-secondary': team?.kit.secondary ?? '#111827',
  } as CSSProperties;
}

function KitChip({
  team,
  showName = false,
  className = '',
}: {
  team: Team | null;
  showName?: boolean;
  className?: string;
}) {
  const classes = ['wc-kit-chip', showName ? 'wc-kit-chip--named' : '', className].filter(Boolean).join(' ');
  return (
    <span className={classes} style={kitVars(team)}>
      <span className="wc-kit-swatch" aria-hidden="true" />
      <span className="wc-kit-code">{team?.code ?? 'TBD'}</span>
      {showName && <span className="wc-kit-name">{team?.name ?? 'TBD'}</span>}
    </span>
  );
}

function winnerTeam(match: Match): Team | null {
  if (match.status !== 'finished' || match.home == null || match.away == null) return null;
  if (match.penalties) {
    if (match.penalties.home === match.penalties.away) return null;
    return getTeam(match.penalties.home > match.penalties.away ? match.home : match.away);
  }
  if (match.homeScore == null || match.awayScore == null || match.homeScore === match.awayScore) return null;
  return getTeam(match.homeScore > match.awayScore ? match.home : match.away);
}

function recentWinners(): Team[] {
  return [...MATCHES]
    .sort((a, b) => kickoffTime(b) - kickoffTime(a))
    .map(winnerTeam)
    .filter((team): team is Team => team != null);
}

function pickLineupTeams(nextMatch: Match | null): [Team, Team] {
  const home = nextMatch ? getTeam(nextMatch.home) : null;
  const away = nextMatch ? getTeam(nextMatch.away) : null;
  if (home && away) return [home, away];
  const winners = recentWinners();
  if (winners.length >= 2) return [winners[0], winners[1]];
  return fallbackPair();
}

function findNextMatch(): Match | null {
  const now = Date.now();
  const upcoming = MATCHES.filter((match) => match.status !== 'finished' && kickoffTime(match) >= now)
    .sort((a, b) => kickoffTime(a) - kickoffTime(b));
  if (upcoming.length > 0) return upcoming[0];
  const anyUpcoming = MATCHES.filter((match) => match.status !== 'finished').sort((a, b) => kickoffTime(a) - kickoffTime(b));
  if (anyUpcoming.length > 0) return anyUpcoming[0];
  return [...MATCHES].sort((a, b) => kickoffTime(b) - kickoffTime(a))[0] ?? null;
}

function todayAndYesterday(now = new Date()): { today: Match[]; yesterday: Match[] } {
  const todayKey = localDateKey(now);
  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  const yesterdayKey = localDateKey(y);
  const sorted = [...MATCHES].sort((a, b) => kickoffTime(a) - kickoffTime(b));
  return {
    today: sorted.filter((match) => localDateKey(new Date(match.kickoffUtc)) === todayKey),
    yesterday: sorted.filter((match) => localDateKey(new Date(match.kickoffUtc)) === yesterdayKey),
  };
}

function teamsStillAlive(): Team[] {
  const stageRank: Record<Stage, number> = { R32: 0, R16: 1, QF: 2, SF: 3, F3: 3, F: 4 };
  const active = MATCHES.filter((match) => match.status !== 'finished' && (match.home || match.away))
    .sort((a, b) => stageRank[b.stage] - stageRank[a.stage]);
  if (active.length > 0) {
    const topRank = stageRank[active[0].stage];
    const codes = new Set<string>();
    active
      .filter((match) => stageRank[match.stage] === topRank)
      .forEach((match) => {
        if (match.home) codes.add(match.home);
        if (match.away) codes.add(match.away);
      });
    const teams = [...codes].map((code) => TEAMS[code]).filter((team): team is Team => team != null);
    if (teams.length > 0) return teams;
  }

  const r16Codes = new Set<string>();
  MATCHES.filter((match) => match.stage === 'R16').forEach((match) => {
    if (match.home) r16Codes.add(match.home);
    if (match.away) r16Codes.add(match.away);
  });
  const r16Teams = [...r16Codes].map((code) => TEAMS[code]).filter((team): team is Team => team != null);
  return r16Teams.length > 0 ? r16Teams : orderedTeams();
}

function readRecord(): ShootoutRecord {
  try {
    const raw = window.localStorage.getItem(RECORD_KEY);
    if (!raw) return { bestScore: 0, gamesPlayed: 0, winStreak: 0 };
    const parsed = JSON.parse(raw) as Partial<ShootoutRecord>;
    return {
      bestScore: typeof parsed.bestScore === 'number' ? parsed.bestScore : 0,
      gamesPlayed: typeof parsed.gamesPlayed === 'number' ? parsed.gamesPlayed : 0,
      winStreak: typeof parsed.winStreak === 'number' ? parsed.winStreak : 0,
    };
  } catch {
    return { bestScore: 0, gamesPlayed: 0, winStreak: 0 };
  }
}

function writeRecord(shots: ShotResult[]): ShootoutRecord {
  const previous = readRecord();
  const goals = shots.filter((shot) => shot === 'goal').length;
  const won = goals >= 3;
  const next = {
    bestScore: Math.max(previous.bestScore, goals),
    gamesPlayed: previous.gamesPlayed + 1,
    winStreak: won ? previous.winStreak + 1 : 0,
  };
  try {
    window.localStorage.setItem(RECORD_KEY, JSON.stringify(next));
  } catch {
    // Storage can be disabled in some embedded browsers; keep the game result visible.
  }
  return next;
}

function weightedKeeperZone(): SaveZone {
  const weighted: Array<[SaveZone, number]> = [
    ['left-high', 1.3],
    ['center-high', 1.45],
    ['right-high', 1.3],
    ['left-low', 1],
    ['center-low', 1.35],
    ['right-low', 1],
  ];
  const total = weighted.reduce((sum, item) => sum + item[1], 0);
  let r = Math.random() * total;
  for (const [zone, weight] of weighted) {
    r -= weight;
    if (r <= 0) return zone;
  }
  return 'center-low';
}

function zoneFromAim(aim: AimPoint): SaveZone {
  const side = aim.x < -0.34 ? 'left' : aim.x > 0.34 ? 'right' : 'center';
  const height = aim.y > 0.53 ? 'high' : 'low';
  return `${side}-${height}` as SaveZone;
}

function zoneCenter(zone: SaveZone): AimPoint {
  const [side, height] = zone.split('-') as ['left' | 'center' | 'right', 'high' | 'low'];
  const x = side === 'left' ? -0.68 : side === 'right' ? 0.68 : 0;
  const y = height === 'high' ? 0.76 : 0.28;
  return { x, y };
}

function decideShot(aim: AimPoint, keeperZone: SaveZone): ShotResult {
  const targetZone = zoneFromAim(aim);
  const extreme = Math.abs(aim.x) > 0.92 || aim.y > 0.93 || aim.y < 0.06;
  if (extreme && Math.random() < 0.2) return 'post';
  if (targetZone === keeperZone) return 'saved';
  const keeper = zoneCenter(keeperZone);
  const keeperNear = Math.abs(keeper.x - aim.x) < 0.28 && Math.abs(keeper.y - aim.y) < 0.24;
  if (keeperNear && Math.random() < 0.62) return 'saved';
  const nearCenter = Math.abs(aim.x) < 0.16 && keeperZone.startsWith('center') && Math.random() < 0.35;
  if (nearCenter) return 'saved';
  return 'goal';
}

function CanvasStateBridge({ mode }: { mode: CupMode }) {
  const ready = useRef(false);
  useFrame(() => {
    if (!ready.current || window.__cupState?.mode !== mode) {
      ready.current = true;
      writeCupState({
        mode,
        dataLoaded: true,
        matchCount: MATCHES.length,
        canvasReady: true,
      });
    }
  });
  return null;
}

function StadiumCamera() {
  const controlsRef = useRef<OrbitControlsImpl | null>(null);
  const resumeAt = useRef(0);
  const { camera, size } = useThree();

  useEffect(() => {
    const portrait = size.height > size.width;
    camera.position.set(portrait ? 0 : 14, portrait ? 22 : 17, portrait ? 36 : 34);
    camera.lookAt(0, 0, 0);
  }, [camera, size.height, size.width]);

  useFrame(() => {
    const controls = controlsRef.current;
    if (!controls) return;
    controls.autoRotate = performance.now() / 1000 > resumeAt.current;
    controls.update();
  });

  return (
    <OrbitControls
      ref={controlsRef}
      enableDamping
      dampingFactor={0.06}
      minDistance={18}
      maxDistance={46}
      maxPolarAngle={Math.PI * 0.46}
      minPolarAngle={Math.PI * 0.2}
      autoRotate
      autoRotateSpeed={0.18}
      onStart={() => {
        resumeAt.current = Number.POSITIVE_INFINITY;
      }}
      onEnd={() => {
        resumeAt.current = performance.now() / 1000 + 4;
      }}
    />
  );
}

function Pitch({ goals = true }: { goals?: boolean } = {}) {
  const stripes = useMemo(() => Array.from({ length: 14 }, (_, i) => i), []);
  return (
    <group>
      {stripes.map((i) => (
        <mesh key={i} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.005, -14 + i * 2 + 1]} receiveShadow>
          <planeGeometry args={[18, 2]} />
          <meshStandardMaterial color={i % 2 === 0 ? '#1aa84f' : '#118640'} roughness={0.82} />
        </mesh>
      ))}
      <mesh position={[0, 0.04, 0]} receiveShadow>
        <boxGeometry args={[18.2, 0.04, 0.08]} />
        <meshStandardMaterial color="#f4f7f1" roughness={0.55} />
      </mesh>
      <mesh position={[0, 0.045, -14]} receiveShadow>
        <boxGeometry args={[18.2, 0.04, 0.08]} />
        <meshStandardMaterial color="#f4f7f1" roughness={0.55} />
      </mesh>
      <mesh position={[0, 0.045, 14]} receiveShadow>
        <boxGeometry args={[18.2, 0.04, 0.08]} />
        <meshStandardMaterial color="#f4f7f1" roughness={0.55} />
      </mesh>
      <mesh position={[-9, 0.045, 0]} receiveShadow>
        <boxGeometry args={[0.08, 0.04, 28.2]} />
        <meshStandardMaterial color="#f4f7f1" roughness={0.55} />
      </mesh>
      <mesh position={[9, 0.045, 0]} receiveShadow>
        <boxGeometry args={[0.08, 0.04, 28.2]} />
        <meshStandardMaterial color="#f4f7f1" roughness={0.55} />
      </mesh>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.055, 0]}>
        <ringGeometry args={[2.05, 2.13, 72]} />
        <meshStandardMaterial color="#f4f7f1" roughness={0.55} />
      </mesh>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.06, 0]}>
        <circleGeometry args={[0.12, 24]} />
        <meshStandardMaterial color="#f4f7f1" roughness={0.55} />
      </mesh>
      {goals &&
        [-1, 1].map((side) => (
          <group key={side} position={[0, 0, side * 15]}>
            <mesh position={[0, 0.9, 0]}>
              <boxGeometry args={[4.8, 0.16, 0.16]} />
              <meshStandardMaterial color="#f7fbff" emissive="#dff6ff" emissiveIntensity={0.18} />
            </mesh>
            <mesh position={[-2.4, 0.45, 0]}>
              <boxGeometry args={[0.16, 0.9, 0.16]} />
              <meshStandardMaterial color="#f7fbff" emissive="#dff6ff" emissiveIntensity={0.18} />
            </mesh>
            <mesh position={[2.4, 0.45, 0]}>
              <boxGeometry args={[0.16, 0.9, 0.16]} />
              <meshStandardMaterial color="#f7fbff" emissive="#dff6ff" emissiveIntensity={0.18} />
            </mesh>
            <mesh position={[0, 0.44, side * 0.22]}>
              <boxGeometry args={[4.5, 0.03, 0.03]} />
              <meshStandardMaterial color="#c7ecff" transparent opacity={0.42} />
            </mesh>
          </group>
        ))}
    </group>
  );
}

function Stands() {
  const tiers = [0, 1, 2];
  return (
    <group>
      {tiers.map((tier) => (
        <group key={tier}>
          {[-1, 1].map((side) => (
            <mesh key={`x-${side}-${tier}`} position={[side * (11.4 + tier * 1.35), 0.5 + tier * 0.58, 0]} castShadow receiveShadow>
              <boxGeometry args={[2.1, 0.85, 35]} />
              <meshStandardMaterial color={tier % 2 === 0 ? '#34302a' : '#443a2f'} roughness={0.78} metalness={0.05} />
            </mesh>
          ))}
          {[-1, 1].map((side) => (
            <mesh key={`z-${side}-${tier}`} position={[0, 0.5 + tier * 0.58, side * (18.0 + tier * 1.35)]} castShadow receiveShadow>
              <boxGeometry args={[28, 0.85, 2.1]} />
              <meshStandardMaterial color={tier % 2 === 0 ? '#38342c' : '#4b4032'} roughness={0.78} metalness={0.05} />
            </mesh>
          ))}
        </group>
      ))}
      <mesh position={[0, -0.08, 0]} receiveShadow>
        <boxGeometry args={[34, 0.16, 42]} />
        <meshStandardMaterial color="#17130f" roughness={0.92} />
      </mesh>
    </group>
  );
}

function Floodlights() {
  const corners: Array<[number, number]> = [
    [-16.5, -20.5],
    [16.5, -20.5],
    [-16.5, 20.5],
    [16.5, 20.5],
  ];
  const bulbs = [-0.54, -0.18, 0.18, 0.54];
  return (
    <>
      {corners.map(([x, z], i) => (
        <group key={i} position={[x, 0, z]}>
          <mesh position={[0, 4.7, 0]} castShadow>
            <boxGeometry args={[0.28, 9.4, 0.28]} />
            <meshStandardMaterial color="#2b3547" metalness={0.45} roughness={0.38} />
          </mesh>
          <mesh position={[0, 9.55, 0]}>
            <boxGeometry args={[1.75, 0.42, 0.18]} />
            <meshStandardMaterial color="#1a2130" metalness={0.4} roughness={0.42} />
          </mesh>
          <mesh position={[0, 9.55, -0.18]}>
            <planeGeometry args={[2.85, 1.05]} />
            <meshBasicMaterial
              color="#ffd36d"
              transparent
              opacity={0.32}
              blending={THREE.AdditiveBlending}
              depthWrite={false}
              side={THREE.DoubleSide}
              toneMapped={false}
            />
          </mesh>
          {bulbs.map((offset) => (
            <mesh key={offset} position={[offset, 9.55, -0.12]}>
              <boxGeometry args={[0.26, 0.26, 0.08]} />
              <meshStandardMaterial color="#fff1c2" emissive="#ffd36d" emissiveIntensity={4.8} toneMapped={false} />
            </mesh>
          ))}
          <pointLight position={[0, 9.2, 0]} intensity={135} distance={32} color="#ffd89a" />
          <spotLight position={[0, 9.6, 0]} angle={0.62} penumbra={0.62} intensity={180} distance={62} color="#fff0bf" castShadow />
        </group>
      ))}
    </>
  );
}

interface CrowdSeat {
  x: number;
  y: number;
  z: number;
  perimeter: number;
  color: THREE.Color;
}

function buildCrowdSeeds(home: Team, away: Team): CrowdSeat[] {
  const seats: CrowdSeat[] = [];
  const count = 3600;
  const teamPalette = [home.kit.primary, away.kit.primary];
  const warmTint = new THREE.Color('#fff3c4');
  for (let i = 0; i < count; i++) {
    const side = i % 4;
    const row = Math.floor((i / 4) % 18);
    const t = ((i * 0.61803398875) % 1 + (row % 3) * 0.015) % 1;
    const jitter = ((i * 17) % 11) * 0.012;
    let x = 0;
    let z = 0;
    let p = 0;
    if (side === 0) {
      x = -10.9 - row * 0.18;
      z = -16.6 + t * 33.2;
      p = t * 0.25;
    } else if (side === 1) {
      x = 10.9 + row * 0.18;
      z = -16.6 + t * 33.2;
      p = 0.5 - t * 0.25;
    } else if (side === 2) {
      x = -13.6 + t * 27.2;
      z = -17.7 - row * 0.18;
      p = 0.75 - t * 0.25;
    } else {
      x = -13.6 + t * 27.2;
      z = 17.7 + row * 0.18;
      p = 0.75 + t * 0.25;
    }
    const teamStripe = i % 3 === 0 || i % 7 === 0;
    const baseColor = teamStripe ? teamPalette[i % teamPalette.length] : FAN_COLORS[i % FAN_COLORS.length];
    const color = new THREE.Color(baseColor).lerp(warmTint, teamStripe ? 0.28 : 0.1);
    seats.push({
      x,
      y: 1.08 + row * 0.115 + jitter,
      z,
      perimeter: p % 1,
      color,
    });
  }
  return seats;
}

function CrowdWave({ home, away }: { home: Team; away: Team }) {
  const meshRef = useRef<THREE.InstancedMesh | null>(null);
  const dummy = useMemo(() => new THREE.Object3D(), []);
  const geometry = useMemo(() => new THREE.BoxGeometry(0.14, 0.2, 0.14), []);
  const material = useMemo(
    () =>
      new THREE.MeshBasicMaterial({
        color: '#ffffff',
        toneMapped: false,
      }),
    [],
  );
  const seats = useMemo(() => buildCrowdSeeds(home, away), [away, home]);

  useEffect(() => {
    const mesh = meshRef.current;
    if (!mesh) return;
    seats.forEach((seat, i) => mesh.setColorAt(i, seat.color));
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  }, [seats]);

  useFrame(({ clock }) => {
    const mesh = meshRef.current;
    if (!mesh) return;
    const crest = (clock.elapsedTime * 0.08) % 1;
    seats.forEach((seat, i) => {
      const d = Math.abs(((seat.perimeter - crest + 1.5) % 1) - 0.5);
      const wave = Math.max(0, 1 - d / 0.055);
      dummy.position.set(seat.x, seat.y + wave * 0.55, seat.z);
      dummy.scale.setScalar(1 + wave * 0.32);
      dummy.updateMatrix();
      mesh.setMatrixAt(i, dummy.matrix);
    });
    mesh.instanceMatrix.needsUpdate = true;
  });

  return <instancedMesh ref={meshRef} args={[geometry, material, seats.length]} castShadow receiveShadow />;
}

function BlockyPlayer({
  team,
  index,
  position,
  rotation,
  phase,
}: {
  team: Team;
  index?: number;
  position: [number, number, number];
  rotation: number;
  phase: number;
}) {
  const ref = useRef<THREE.Group | null>(null);
  useFrame(({ clock }) => {
    const group = ref.current;
    if (!group) return;
    const runtime = getExistingCupPhysicsRuntime();
    if (typeof index === 'number' && runtime?.ambientReady) {
      const pose = stadiumPlayerRenderPose(index, runtime.fixedTime, runtime);
      group.position.set(pose.x, pose.y, pose.z);
      group.rotation.set(0, pose.rotation, 0);
      return;
    }

    group.position.set(position[0], position[1] + Math.sin(clock.elapsedTime * 2.4 + phase) * 0.045, position[2]);
    group.rotation.set(0, rotation, 0);
  });

  return (
    <group ref={ref} position={position} rotation={[0, rotation, 0]}>
      <mesh position={[0, 0.54, 0]} castShadow>
        <boxGeometry args={[0.42, 0.62, 0.24]} />
        <meshStandardMaterial color={team.kit.primary} roughness={0.58} />
      </mesh>
      <mesh position={[0, 0.17, 0]} castShadow>
        <boxGeometry args={[0.44, 0.28, 0.25]} />
        <meshStandardMaterial color={team.kit.secondary} roughness={0.64} />
      </mesh>
      <mesh position={[0, 0.96, 0]} castShadow>
        <boxGeometry args={[0.28, 0.28, 0.28]} />
        <meshStandardMaterial color="#d9aa7f" roughness={0.82} />
      </mesh>
      <mesh position={[-0.28, 0.48, 0]} castShadow>
        <boxGeometry args={[0.12, 0.44, 0.13]} />
        <meshStandardMaterial color={team.kit.primary} roughness={0.62} />
      </mesh>
      <mesh position={[0.28, 0.48, 0]} castShadow>
        <boxGeometry args={[0.12, 0.44, 0.13]} />
        <meshStandardMaterial color={team.kit.primary} roughness={0.62} />
      </mesh>
      <mesh position={[-0.12, -0.13, 0]} castShadow>
        <boxGeometry args={[0.14, 0.34, 0.14]} />
        <meshStandardMaterial color={team.kit.secondary} roughness={0.66} />
      </mesh>
      <mesh position={[0.12, -0.13, 0]} castShadow>
        <boxGeometry args={[0.14, 0.34, 0.14]} />
        <meshStandardMaterial color={team.kit.secondary} roughness={0.66} />
      </mesh>
    </group>
  );
}

function TeamLineups({ home, away }: { home: Team; away: Team }) {
  return (
    <group>
      {STADIUM_PLAYER_LAYOUT.map((player, i) => (
        <BlockyPlayer
          key={`${player.side}-${i}`}
          team={player.side === 'home' ? home : away}
          index={i}
          position={[player.x, 0.35, player.z]}
          rotation={player.rotation}
          phase={player.phase}
        />
      ))}
    </group>
  );
}

function StadiumPhysicsLayer({
  onReady,
  onUnavailable,
  onIntroComplete,
  onFanBallCount,
}: {
  onReady: (runtime: CupPhysicsRuntime) => void;
  onUnavailable: () => void;
  onIntroComplete: () => void;
  onFanBallCount: (count: number) => void;
}) {
  const runtimeRef = useRef<CupPhysicsRuntime | null>(getExistingCupPhysicsRuntime());
  const introMeshRef = useRef<THREE.InstancedMesh | null>(null);
  const fanMeshRef = useRef<THREE.InstancedMesh | null>(null);
  const matchBallRef = useRef<THREE.Mesh | null>(null);
  const readyNotifiedRef = useRef(false);
  const introNotifiedRef = useRef(runtimeRef.current?.lineupVisible ?? false);
  const fanCountRef = useRef(runtimeRef.current?.fanBalls.length ?? 0);
  const introClearedRef = useRef(false);
  const introColorsReadyRef = useRef(false);
  const dummy = useMemo(() => new THREE.Object3D(), []);
  const color = useMemo(() => new THREE.Color(), []);
  const introGeometry = useMemo(() => new THREE.BoxGeometry(0.53, 0.36, 0.53), []);
  const introMaterial = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        roughness: 0.62,
        metalness: 0.08,
        transparent: true,
      }),
    [],
  );
  const fanGeometry = useMemo(() => new THREE.SphereGeometry(1, 28, 18), []);
  const fanMaterial = useMemo(
    () => new THREE.MeshStandardMaterial({ roughness: 0.34, metalness: 0.04 }),
    [],
  );

  useEffect(() => {
    const hideInstances = (mesh: THREE.InstancedMesh | null, count: number) => {
      if (!mesh) return;
      for (let i = 0; i < count; i += 1) {
        dummy.position.set(0, -80, 0);
        dummy.scale.setScalar(0.001);
        dummy.updateMatrix();
        mesh.setMatrixAt(i, dummy.matrix);
      }
      mesh.instanceMatrix.needsUpdate = true;
    };

    hideInstances(introMeshRef.current, INTRO_BOX_COUNT);
    hideInstances(fanMeshRef.current, FAN_BALL_LIMIT);
  }, [dummy]);

  useEffect(() => {
    let cancelled = false;
    const existing = getExistingCupPhysicsRuntime();
    if (existing) {
      runtimeRef.current = existing;
      if (!readyNotifiedRef.current) {
        readyNotifiedRef.current = true;
        onReady(existing);
        onFanBallCount(existing.fanBalls.length);
      }
      if (existing.lineupVisible && !introNotifiedRef.current) {
        introNotifiedRef.current = true;
        onIntroComplete();
      }
      return () => {
        cancelled = true;
      };
    }

    getCupPhysicsRuntime()
      .then((runtime) => {
        if (cancelled) return;
        runtimeRef.current = runtime;
        if (!readyNotifiedRef.current) {
          readyNotifiedRef.current = true;
          onReady(runtime);
          onFanBallCount(runtime.fanBalls.length);
        }
      })
      .catch((error: unknown) => {
        console.warn('Cup physics unavailable; falling back to static stadium.', error);
        if (!cancelled) {
          onUnavailable();
        }
      });

    return () => {
      cancelled = true;
    };
  }, [onFanBallCount, onIntroComplete, onReady, onUnavailable]);

  useFrame((_, delta) => {
    const runtime = runtimeRef.current;
    if (!runtime) return;

    advanceCupPhysics(runtime, delta);

    if (import.meta.env.DEV) {
      const cupDebugKey = `__cup${'Debug'}`;
      const debugWindow = window as unknown as Record<string, CupDebugState>;
      debugWindow[cupDebugKey] = {
        fixedTime: runtime.fixedTime,
        ambientReady: runtime.ambientReady,
        introPhase: runtime.introPhase,
        ball: { ...runtime.matchBallPosition },
        introBlocks: runtime.introBoxes.map((box) => ({ x: box.currentX, y: box.currentY, z: box.currentZ })),
        introTargets: runtime.introBoxes.map((box) => ({ x: box.targetX, y: box.targetY, z: box.targetZ })),
        kick: {
          active: runtime.kick.active,
          playerIndex: runtime.kick.playerIndex,
          phase: runtime.kick.phase,
          nearestPlayer: { ...runtime.kick.nearestPlayer },
          ballAtStrike: runtime.kick.ballAtStrike ? { ...runtime.kick.ballAtStrike } : null,
        },
      };
    }

    if (runtime.lineupVisible && !introNotifiedRef.current) {
      introNotifiedRef.current = true;
      onIntroComplete();
    }

    if (runtime.fanBalls.length !== fanCountRef.current) {
      fanCountRef.current = runtime.fanBalls.length;
      onFanBallCount(runtime.fanBalls.length);
    }

    const introMesh = introMeshRef.current;
    const fanMesh = fanMeshRef.current;
    const matchBall = matchBallRef.current;

    if (introMesh && !introColorsReadyRef.current) {
      runtime.introBoxes.forEach((box, i) => {
        introMesh.setColorAt(i, color.set(box.color));
      });
      if (introMesh.instanceColor) introMesh.instanceColor.needsUpdate = true;
      introColorsReadyRef.current = true;
    }

    introMaterial.opacity = runtime.introFade;

    let matchUpdated = false;
    for (let i = 0; i < runtime.records.length; i += 1) {
      const record = runtime.records[i];
      const offset = i * 7;
      dummy.position.set(runtime.transforms[offset], runtime.transforms[offset + 1], runtime.transforms[offset + 2]);
      dummy.quaternion.set(runtime.transforms[offset + 3], runtime.transforms[offset + 4], runtime.transforms[offset + 5], runtime.transforms[offset + 6]);

      if (record.kind === 'introBox' && introMesh) {
        dummy.scale.setScalar(1);
        dummy.updateMatrix();
        introMesh.setMatrixAt(record.index, dummy.matrix);
      } else if (record.kind === 'fanBall' && fanMesh) {
        const ball = runtime.fanBalls[record.index];
        if (!ball) continue;
        dummy.scale.setScalar(ball.radius * (1 + ball.pulse));
        dummy.updateMatrix();
        fanMesh.setMatrixAt(record.index, dummy.matrix);
        fanMesh.setColorAt(record.index, color.set(ball.color));
      } else if (record.kind === 'matchBall' && matchBall) {
        matchBall.visible = true;
        matchBall.position.copy(dummy.position);
        matchBall.quaternion.copy(dummy.quaternion);
        matchBall.scale.setScalar(1 + runtime.matchBallPulse);
        matchUpdated = true;
      }
    }

    if (runtime.introBodiesDestroyed && introMesh && !introClearedRef.current) {
      for (let i = 0; i < INTRO_BOX_COUNT; i += 1) {
        dummy.position.set(0, -80, 0);
        dummy.scale.setScalar(0.001);
        dummy.updateMatrix();
        introMesh.setMatrixAt(i, dummy.matrix);
      }
      introClearedRef.current = true;
    }

    if (fanMesh) {
      for (let i = runtime.fanBalls.length; i < FAN_BALL_LIMIT; i += 1) {
        dummy.position.set(0, -80, 0);
        dummy.scale.setScalar(0.001);
        dummy.updateMatrix();
        fanMesh.setMatrixAt(i, dummy.matrix);
      }
      fanMesh.instanceMatrix.needsUpdate = true;
      if (fanMesh.instanceColor) fanMesh.instanceColor.needsUpdate = true;
    }

    if (introMesh) {
      introMesh.instanceMatrix.needsUpdate = true;
    }

    if (matchBall && !matchUpdated) {
      matchBall.visible = false;
    }
  });

  return (
    <>
      <mesh
        rotation={[-Math.PI / 2, 0, 0]}
        position={[0, 0.08, 0]}
        onClick={(event) => {
          const runtime = runtimeRef.current;
          if (!runtime) return;
          event.stopPropagation();
          const count = spawnFanBall(runtime, event.point);
          onFanBallCount(count);
        }}
      >
        <planeGeometry args={[34, 42]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} />
      </mesh>
      <instancedMesh ref={introMeshRef} args={[introGeometry, introMaterial, INTRO_BOX_COUNT]} castShadow receiveShadow />
      <instancedMesh ref={fanMeshRef} args={[fanGeometry, fanMaterial, FAN_BALL_LIMIT]} castShadow receiveShadow />
      <mesh ref={matchBallRef} visible={false} castShadow receiveShadow>
        <sphereGeometry args={[0.28, 32, 20]} />
        <meshStandardMaterial color="#f8fbff" roughness={0.28} metalness={0.06} />
      </mesh>
    </>
  );
}

function StadiumScene({
  lineup,
  onPhysicsReady,
  onPhysicsUnavailable,
  onFanBallCount,
}: {
  lineup: [Team, Team];
  onPhysicsReady: (runtime: CupPhysicsRuntime) => void;
  onPhysicsUnavailable: () => void;
  onFanBallCount: (count: number) => void;
}) {
  const [lineupVisible, setLineupVisible] = useState(() => getExistingCupPhysicsRuntime()?.lineupVisible ?? false);

  const handleIntroComplete = useCallback(() => {
    setLineupVisible(true);
  }, []);

  const handlePhysicsUnavailable = useCallback(() => {
    setLineupVisible(true);
    onPhysicsUnavailable();
  }, [onPhysicsUnavailable]);

  return (
    <>
      <color attach="background" args={['#07131f']} />
      <fog attach="fog" args={['#17120a', 27, 68]} />
      <ambientLight intensity={0.72} color="#ffdba4" />
      <hemisphereLight args={['#7fb9ff', '#2a1909', 1.65]} />
      <directionalLight position={[0, 14, 7]} intensity={1.8} color="#fff1c7" />
      <StadiumCamera />
      <Stands />
      <Pitch />
      <Floodlights />
      <CrowdWave home={lineup[0]} away={lineup[1]} />
      <StadiumPhysicsLayer
        onReady={onPhysicsReady}
        onUnavailable={handlePhysicsUnavailable}
        onIntroComplete={handleIntroComplete}
        onFanBallCount={onFanBallCount}
      />
      {lineupVisible && <TeamLineups home={lineup[0]} away={lineup[1]} />}
      <ContactShadows position={[0, 0.02, 0]} opacity={0.35} scale={34} blur={2.6} far={10} />
    </>
  );
}

function BracketCamera() {
  const { camera, size } = useThree();
  useEffect(() => {
    const portrait = size.height > size.width;
    camera.position.set(0, portrait ? -1.2 : 0, portrait ? 58 : 42);
    camera.lookAt(0, 0, 0);
  }, [camera, size.height, size.width]);
  return (
    <OrbitControls
      enableDamping
      dampingFactor={0.08}
      enablePan
      minDistance={18}
      maxDistance={70}
      minPolarAngle={Math.PI * 0.42}
      maxPolarAngle={Math.PI * 0.58}
    />
  );
}

function bracketLayout(matches: Match[]): Map<string, THREE.Vector3> {
  const byStage = new Map<Stage, Match[]>();
  [...STAGES, 'F3' as Stage].forEach((stage) => byStage.set(stage, []));
  matches.forEach((match) => {
    byStage.get(match.stage)?.push(match);
  });
  byStage.forEach((stageMatches) => {
    stageMatches.sort((a, b) => kickoffTime(a) - kickoffTime(b) || a.id.localeCompare(b.id));
  });

  const result = new Map<string, THREE.Vector3>();
  byStage.forEach((stageMatches, stage) => {
    if (stage === 'F') {
      stageMatches.forEach((match, i) => result.set(match.id, new THREE.Vector3(BRACKET_X.F, i === 0 ? 2.8 : 1.2 - i * 1.6, 0)));
      return;
    }
    if (stage === 'F3') {
      stageMatches.forEach((match, i) => result.set(match.id, new THREE.Vector3(BRACKET_X.F3, -3.4 - i * 1.6, 0)));
      return;
    }
    const spacing = stage === 'R32' ? 1.72 : stage === 'R16' ? 2.72 : stage === 'QF' ? 4.3 : 6.8;
    const center = (stageMatches.length - 1) * spacing * 0.5;
    stageMatches.forEach((match, i) => result.set(match.id, new THREE.Vector3(BRACKET_X[stage], center - i * spacing, 0)));
  });
  return result;
}

function Connection({ from, to, color }: { from: THREE.Vector3; to: THREE.Vector3; color: string }) {
  const { mid, length, quaternion } = useMemo(() => {
    const start = from.clone();
    const end = to.clone();
    start.x += 1.8;
    end.x -= 1.8;
    const delta = end.clone().sub(start);
    const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), delta.clone().normalize());
    return {
      mid: start.clone().add(end).multiplyScalar(0.5),
      length: delta.length(),
      quaternion: q,
    };
  }, [from, to]);

  return (
    <group position={mid} quaternion={quaternion}>
      <mesh>
        <cylinderGeometry args={[0.095, 0.095, length, 12]} />
        <meshBasicMaterial color={color} transparent opacity={0.2} />
      </mesh>
      <mesh>
        <cylinderGeometry args={[0.028, 0.028, length, 10]} />
        <meshStandardMaterial color={color} emissive={color} emissiveIntensity={2.4} roughness={0.22} toneMapped={false} />
      </mesh>
    </group>
  );
}

function BracketCard({
  match,
  position,
  selected,
  onSelect,
}: {
  match: Match;
  position: THREE.Vector3;
  selected: boolean;
  onSelect: (match: Match) => void;
}) {
  const home = getTeam(match.home);
  const away = getTeam(match.away);
  const finished = match.status === 'finished';

  return (
    <group position={position}>
      <mesh castShadow receiveShadow>
        <boxGeometry args={[3.55, 1.02, 0.14]} />
        <meshStandardMaterial
          color={selected ? '#1b2a3f' : finished ? '#111a27' : '#0b111d'}
          emissive={selected ? '#57ff6a' : '#000000'}
          emissiveIntensity={selected ? 0.28 : 0}
          roughness={0.58}
          metalness={0.24}
        />
      </mesh>
      <Html center position={[0, 0, 0.24]} zIndexRange={[20, 10]}>
        <button
          className={`wc-bracket-card3d ${selected ? 'selected' : ''} ${finished ? 'finished' : 'upcoming'}`}
          data-testid={`bracket-card-${match.id}`}
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onSelect(match);
          }}
        >
          <span className="wc-bracket-stage-mini">{match.stage === 'F3' ? '3P' : match.stage}</span>
          <span className="wc-bracket-team">
            <span className="wc-bracket-swatch" style={kitVars(home)} />
            <span>{home?.code ?? 'TBD'}</span>
            <strong>{match.homeScore ?? '-'}</strong>
          </span>
          <span className="wc-bracket-team">
            <span className="wc-bracket-swatch" style={kitVars(away)} />
            <span>{away?.code ?? 'TBD'}</span>
            <strong>{match.awayScore ?? '-'}</strong>
          </span>
        </button>
      </Html>
    </group>
  );
}

function BracketBackdrop() {
  return (
    <group position={[0, -15.2, -2.8]}>
      <mesh>
        <planeGeometry args={[22, 5.8]} />
        <meshStandardMaterial color="#0f7d38" emissive="#0c4d26" emissiveIntensity={0.18} roughness={0.88} />
      </mesh>
      <mesh position={[0, 0, 0.05]}>
        <ringGeometry args={[0.82, 0.9, 48]} />
        <meshBasicMaterial color="#e7ead8" transparent opacity={0.6} />
      </mesh>
      {[-10, -5, 0, 5, 10].map((x) => (
        <mesh key={x} position={[x, 0, 0.06]}>
          <boxGeometry args={[0.05, 5.8, 0.03]} />
          <meshBasicMaterial color="#e7ead8" transparent opacity={0.22} />
        </mesh>
      ))}
      <pointLight position={[0, 4, 5]} intensity={38} color="#ffd36d" />
    </group>
  );
}

function BracketScene({ selectedId, onSelect }: { selectedId: string | null; onSelect: (match: Match) => void }) {
  const positions = useMemo(() => bracketLayout(MATCHES), []);
  const connections = useMemo(
    () =>
      MATCHES.flatMap((match) => {
        if (!match.feedsInto) return [];
        const from = positions.get(match.id);
        const to = positions.get(match.feedsInto);
        if (!from || !to) return [];
        const winner = winnerTeam(match);
        const color = winner?.kit.primary ?? getTeam(match.home)?.kit.primary ?? '#31d7ff';
        return [{ id: `${match.id}-${match.feedsInto}`, from, to, color }];
      }),
    [positions],
  );

  return (
    <>
      <color attach="background" args={['#050915']} />
      <fog attach="fog" args={['#050915', 38, 82]} />
      <ambientLight intensity={0.94} color="#d7e5ff" />
      <pointLight position={[0, 7, 16]} intensity={72} color="#42d9ff" />
      <pointLight position={[0, -7, 10]} intensity={60} color="#ffd36d" />
      <BracketCamera />
      <group position={[0, 0, 0]}>
        <BracketBackdrop />
        {STAGES.map((stage) => (
          <Html key={stage} center position={[BRACKET_X[stage], 15.45, 0.2]} zIndexRange={[12, 4]}>
            <div className="wc-bracket-stage-label">{BRACKET_STAGE_LABEL[stage]}</div>
          </Html>
        ))}
        <Html center position={[15, -5.4, 0.2]} zIndexRange={[12, 4]}>
          <div className="wc-bracket-stage-label small">Third Place</div>
        </Html>
        {connections.map((line) => (
          <Connection key={line.id} from={line.from} to={line.to} color={line.color} />
        ))}
        {MATCHES.map((match) => {
          const position = positions.get(match.id) ?? new THREE.Vector3(0, 0, 0);
          const lifted = selectedId === match.id ? position.clone().add(new THREE.Vector3(0, 0, 0.25)) : position;
          return <BracketCard key={match.id} match={match} position={lifted} selected={selectedId === match.id} onSelect={onSelect} />;
        })}
      </group>
    </>
  );
}

function ShootoutCamera() {
  const { camera, size } = useThree();
  useEffect(() => {
    const portrait = size.height > size.width;
    camera.position.set(0, portrait ? 4.7 : 3.4, portrait ? 13.5 : 12);
    camera.lookAt(0, 1.65, -8.5);
  }, [camera, size.height, size.width]);
  return null;
}

function GoalNet() {
  const texture = useMemo(() => {
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 256;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = 'rgba(245, 248, 231, 0.88)';
      ctx.lineWidth = 2;
      for (let x = 0; x <= canvas.width; x += 32) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, canvas.height);
        ctx.stroke();
      }
      for (let y = 0; y <= canvas.height; y += 24) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(canvas.width, y);
        ctx.stroke();
      }
    }
    const netTexture = new THREE.CanvasTexture(canvas);
    netTexture.colorSpace = THREE.SRGBColorSpace;
    return netTexture;
  }, []);

  useEffect(() => () => texture.dispose(), [texture]);

  return (
    <mesh position={[0, 1.72, 0.05]}>
      <planeGeometry args={[6.7, 3.35]} />
      <meshBasicMaterial map={texture} transparent opacity={0.58} side={THREE.DoubleSide} depthWrite={false} />
    </mesh>
  );
}

function Keeper({ groupRef }: { groupRef: MutableRefObject<THREE.Group | null> }) {
  return (
    <group ref={groupRef} position={[0, 1.0, -11.55]}>
      <mesh position={[0, 0.1, 0]} castShadow>
        <boxGeometry args={[0.72, 0.82, 0.28]} />
        <meshStandardMaterial color="#d7193f" emissive="#3f0612" emissiveIntensity={0.25} roughness={0.56} />
      </mesh>
      <mesh position={[0, -0.42, 0]} castShadow>
        <boxGeometry args={[0.76, 0.28, 0.28]} />
        <meshStandardMaterial color="#7a0f27" roughness={0.62} />
      </mesh>
      <mesh position={[0, 0.72, 0]} castShadow>
        <boxGeometry args={[0.36, 0.32, 0.3]} />
        <meshStandardMaterial color="#d9aa7f" roughness={0.82} />
      </mesh>
      <mesh position={[-0.55, 0.14, 0]} rotation={[0, 0, -0.18]} castShadow>
        <boxGeometry args={[0.2, 0.82, 0.18]} />
        <meshStandardMaterial color="#d7193f" roughness={0.58} />
      </mesh>
      <mesh position={[0.55, 0.14, 0]} rotation={[0, 0, 0.18]} castShadow>
        <boxGeometry args={[0.2, 0.82, 0.18]} />
        <meshStandardMaterial color="#d7193f" roughness={0.58} />
      </mesh>
      {[-0.7, 0.7].map((x) => (
        <mesh key={`glove-${x}`} position={[x, -0.22, 0]} castShadow>
          <boxGeometry args={[0.24, 0.22, 0.2]} />
          <meshStandardMaterial color="#f7fbff" roughness={0.36} />
        </mesh>
      ))}
      {[-0.2, 0.2].map((x) => (
        <mesh key={`leg-${x}`} position={[x, -0.82, 0]} castShadow>
          <boxGeometry args={[0.2, 0.58, 0.2]} />
          <meshStandardMaterial color="#7a0f27" roughness={0.64} />
        </mesh>
      ))}
      {[-0.2, 0.2].map((x) => (
        <mesh key={`boot-${x}`} position={[x, -1.16, 0.08]} castShadow>
          <boxGeometry args={[0.28, 0.14, 0.32]} />
          <meshStandardMaterial color="#10131a" roughness={0.52} />
        </mesh>
      ))}
      <mesh position={[0, 0.28, 0.16]}>
        <boxGeometry args={[0.5, 0.08, 0.03]} />
        <meshStandardMaterial color="#ffcf5a" emissive="#ffcf5a" emissiveIntensity={0.35} />
      </mesh>
    </group>
  );
}

function Striker({ team }: { team: Team }) {
  return (
    <group position={[0, 0.36, 5.1]} rotation={[0, Math.PI, 0]}>
      <BlockyPlayer team={team} position={[0, 0, 0]} rotation={0} phase={0.2} />
      <mesh position={[-0.22, -0.48, -0.2]} rotation={[0.2, 0.1, -0.4]} castShadow>
        <boxGeometry args={[0.14, 0.46, 0.14]} />
        <meshStandardMaterial color={team.kit.secondary} roughness={0.64} />
      </mesh>
    </group>
  );
}

interface ConfettiPiece {
  position: THREE.Vector3;
  velocity: THREE.Vector3;
  color: THREE.Color;
}

function ShootoutScene({
  team,
  aimRef,
  eventRef,
}: {
  team: Team;
  aimRef: MutableRefObject<AimPoint>;
  eventRef: MutableRefObject<ShootoutEvent | null>;
}) {
  const ballRef = useRef<THREE.Mesh | null>(null);
  const reticleRef = useRef<THREE.Group | null>(null);
  const keeperRef = useRef<THREE.Group | null>(null);
  const confettiRef = useRef<THREE.InstancedMesh | null>(null);
  const dummy = useMemo(() => new THREE.Object3D(), []);
  const motion = useRef<{ token: number; start: number; target: THREE.Vector3; keeper: AimPoint; outcome: ShotResult } | null>(null);
  const lastToken = useRef(0);
  const piecesRef = useRef<ConfettiPiece[]>([]);
  const confettiGeometry = useMemo(() => new THREE.BoxGeometry(0.08, 0.08, 0.02), []);
  const confettiMaterial = useMemo(
    () => new THREE.MeshStandardMaterial({ roughness: 0.54, metalness: 0.08, vertexColors: true }),
    [],
  );

  useEffect(() => {
    const mesh = confettiRef.current;
    if (!mesh) return;
    for (let i = 0; i < 90; i++) {
      dummy.position.set(0, -10, 0);
      dummy.scale.setScalar(0.01);
      dummy.updateMatrix();
      mesh.setMatrixAt(i, dummy.matrix);
      mesh.setColorAt(i, new THREE.Color(FAN_COLORS[i % FAN_COLORS.length]));
    }
    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  }, [dummy]);

  useFrame(({ clock }, dt) => {
    const event = eventRef.current;
    if (event && event.token !== lastToken.current) {
      lastToken.current = event.token;
      const target = new THREE.Vector3(event.target.x * 3.05, 1.02 + event.target.y * 2.32, -11.7);
      motion.current = {
        token: event.token,
        start: clock.elapsedTime,
        target,
        keeper: zoneCenter(event.keeperZone),
        outcome: event.outcome,
      };
      if (event.outcome === 'goal') {
        piecesRef.current = Array.from({ length: 90 }, (_, i) => ({
          position: target.clone(),
          velocity: new THREE.Vector3((Math.random() - 0.5) * 6, Math.random() * 3 + 1.8, (Math.random() - 0.5) * 2),
          color: new THREE.Color(FAN_COLORS[i % FAN_COLORS.length]),
        }));
      }
    }

    const aim = aimRef.current;
    if (reticleRef.current) {
      reticleRef.current.position.set(aim.x * 3.05, 1.02 + aim.y * 2.32, -11.62);
    }

    const ball = ballRef.current;
    const keeper = keeperRef.current;
    const active = motion.current;
    const start = new THREE.Vector3(0, 0.36, 4.15);
    if (active && ball && keeper) {
      const t = THREE.MathUtils.clamp((clock.elapsedTime - active.start) / 0.52, 0, 1);
      ball.position.lerpVectors(start, active.target, t);
      ball.position.y += Math.sin(t * Math.PI) * 1.15;
      const kx = active.keeper.x * 2.2;
      const ky = 0.78 + active.keeper.y * 1.2;
      keeper.position.lerp(new THREE.Vector3(kx, ky, -11.55), 0.22);
      keeper.rotation.z = THREE.MathUtils.lerp(keeper.rotation.z, -active.keeper.x * 0.65, 0.2);
      if (clock.elapsedTime - active.start > 1.15) {
        motion.current = null;
      }
    } else {
      if (ball) ball.position.lerp(start, 0.12);
      if (keeper) {
        keeper.position.lerp(new THREE.Vector3(0, 1.0, -11.55), 0.08);
        keeper.rotation.z = THREE.MathUtils.lerp(keeper.rotation.z, 0, 0.1);
      }
    }

    const confetti = confettiRef.current;
    const pieces = piecesRef.current;
    if (confetti && pieces.length > 0) {
      pieces.forEach((piece, i) => {
        piece.velocity.y -= 3.2 * dt;
        piece.position.addScaledVector(piece.velocity, dt);
        dummy.position.copy(piece.position);
        dummy.rotation.set(clock.elapsedTime * (1.3 + i * 0.02), i, clock.elapsedTime * 2);
        dummy.scale.setScalar(Math.max(0.01, 1 - Math.max(0, piece.position.y - 0.2) * 0.03));
        dummy.updateMatrix();
        confetti.setMatrixAt(i, dummy.matrix);
        confetti.setColorAt(i, piece.color);
      });
      piecesRef.current = pieces.filter((piece) => piece.position.y > -0.4);
      confetti.instanceMatrix.needsUpdate = true;
      if (confetti.instanceColor) confetti.instanceColor.needsUpdate = true;
    }
  });

  return (
    <>
      <color attach="background" args={['#07111c']} />
      <fog attach="fog" args={['#0b1520', 22, 48]} />
      <ambientLight intensity={0.72} color="#ffdba4" />
      <hemisphereLight args={['#8bc4ff', '#1e160c', 1.2]} />
      <pointLight position={[0, 5.8, -8]} intensity={88} color="#ffdf9e" />
      <pointLight position={[0, 2.2, 6.4]} intensity={30} color="#70d7ff" />
      <spotLight position={[-4.8, 6.5, 1]} angle={0.5} penumbra={0.45} intensity={120} distance={34} color="#fff2c2" castShadow />
      <spotLight position={[4.8, 6.5, 1]} angle={0.5} penumbra={0.45} intensity={120} distance={34} color="#fff2c2" castShadow />
      <ShootoutCamera />
      <Stands />
      <Pitch goals={false} />
      <Floodlights />
      <CrowdWave home={team} away={DEFAULT_TEAMS[1]} />
      <group position={[0, 0, -12]}>
        <GoalNet />
        <mesh position={[0, 3.42, 0]}>
          <boxGeometry args={[6.8, 0.12, 0.14]} />
          <meshStandardMaterial color="#f7fbff" emissive="#ccf4ff" emissiveIntensity={0.45} />
        </mesh>
        {[-3.4, 3.4].map((x) => (
          <mesh key={x} position={[x, 1.72, 0]}>
            <boxGeometry args={[0.12, 3.4, 0.14]} />
            <meshStandardMaterial color="#f7fbff" emissive="#ccf4ff" emissiveIntensity={0.45} />
          </mesh>
        ))}
      </group>
      <Keeper groupRef={keeperRef} />
      <Striker team={team} />
      <mesh ref={ballRef} position={[0, 0.36, 4.15]} castShadow>
        <sphereGeometry args={[0.18, 24, 16]} />
        <meshStandardMaterial color="#f7fbff" roughness={0.36} metalness={0.08} />
      </mesh>
      <group ref={reticleRef} position={[0, 2.1, -11.62]}>
        <mesh>
          <ringGeometry args={[0.2, 0.24, 32]} />
          <meshBasicMaterial color="#ffcf5a" transparent opacity={0.95} />
        </mesh>
        <mesh>
          <ringGeometry args={[0.34, 0.35, 32]} />
          <meshBasicMaterial color="#31d7ff" transparent opacity={0.45} />
        </mesh>
      </group>
      <instancedMesh ref={confettiRef} args={[confettiGeometry, confettiMaterial, 90]} />
      <ContactShadows position={[0, 0.02, 0]} opacity={0.4} scale={28} blur={2.4} far={8} />
    </>
  );
}

function Scoreboard({ match }: { match: Match | null }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const home = match ? getTeam(match.home) : null;
  const away = match ? getTeam(match.away) : null;
  const parts = match ? countdownParts(now, match.kickoffUtc) : countdownParts(now, '');

  return (
    <section className="wc-scoreboard" aria-label="Next match scoreboard">
      <div className="wc-score-kicker">Next Match</div>
      <div className="wc-score-teams">
        <span className="wc-score-team">
          <KitChip team={home} />
          <span>{home?.name ?? 'TBD'}</span>
        </span>
        <strong>VS</strong>
        <span className="wc-score-team">
          <KitChip team={away} />
          <span>{away?.name ?? 'TBD'}</span>
        </span>
      </div>
      <div className="wc-score-meta">{match ? `${match.venue} - ${match.city}` : 'Schedule pending'}</div>
      <div className="wc-countdown" aria-label="Countdown to kickoff">
        {parts.map((part) => (
          <span key={part.label} className="wc-countdown-unit">
            <strong>{part.value}</strong>
            <em>{part.label}</em>
          </span>
        ))}
      </div>
    </section>
  );
}

function MatchRow({ match }: { match: Match }) {
  const home = getTeam(match.home);
  const away = getTeam(match.away);
  const rightLabel = match.status === 'finished' || match.status === 'live' ? scoreText(match) : BRACKET_STAGE_LABEL[match.stage];

  return (
    <li className={`wc-match-row ${match.status}`}>
      <span className="wc-match-time">{formatLocalTime(match.kickoffUtc)}</span>
      <span className="wc-match-name">
        <KitChip team={home} />
        <span className="wc-match-vs">vs</span>
        <KitChip team={away} />
      </span>
      <span className="wc-match-score">{rightLabel}</span>
    </li>
  );
}

function TodayPanel() {
  const [{ today, yesterday }, setMatches] = useState(() => todayAndYesterday());
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    const id = window.setInterval(() => setMatches(todayAndYesterday()), 1000);
    return () => window.clearInterval(id);
  }, []);

  return (
    <aside className={`wc-today ${collapsed ? 'collapsed' : ''}`} data-testid="today-panel">
      <button className="wc-panel-toggle" type="button" onClick={() => setCollapsed((value) => !value)}>
        Today at the Cup
      </button>
      <div className="wc-panel-body">
        <div className="wc-panel-section">
          <h2>Today</h2>
          {today.length > 0 ? <ul>{today.map((match) => <MatchRow key={match.id} match={match} />)}</ul> : <p>No fixtures today.</p>}
        </div>
        <div className="wc-panel-section">
          <h2>Yesterday</h2>
          {yesterday.length > 0 ? <ul>{yesterday.map((match) => <MatchRow key={match.id} match={match} />)}</ul> : <p>No results yesterday.</p>}
        </div>
        <div className="wc-updated">Data updated {formatUpdated(LAST_UPDATED)}</div>
      </div>
    </aside>
  );
}

function BracketDetail({ match }: { match: Match | null }) {
  if (!match) return null;
  const home = getTeam(match.home);
  const away = getTeam(match.away);
  return (
    <aside className="wc-bracket-detail" data-testid="bracket-detail">
      <div className="wc-detail-stage">{STAGE_LABEL[match.stage]}</div>
      <h2>{matchName(match)}</h2>
      <div className="wc-detail-score">{scoreText(match)}</div>
      <dl>
        <div><dt>Home</dt><dd><KitChip team={home} showName /></dd></div>
        <div><dt>Away</dt><dd><KitChip team={away} showName /></dd></div>
        <div><dt>Venue</dt><dd>{match.venue}</dd></div>
        <div><dt>City</dt><dd>{match.city}</dd></div>
        <div><dt>Kickoff</dt><dd>{formatShortDate(match.kickoffUtc)} {formatLocalTime(match.kickoffUtc)}</dd></div>
      </dl>
    </aside>
  );
}

function ShootoutHud({
  phase,
  teams,
  selectedTeam,
  shots,
  feedback,
  record,
  onPick,
  onShoot,
  onAim,
  onAgain,
  onChangeTeam,
}: {
  phase: ShootoutPhase;
  teams: Team[];
  selectedTeam: Team | null;
  shots: ShotResult[];
  feedback: string;
  record: ShootoutRecord;
  onPick: (team: Team) => void;
  onShoot: () => void;
  onAim: (event: React.PointerEvent<HTMLDivElement>) => void;
  onAgain: () => void;
  onChangeTeam: () => void;
}) {
  const goals = shots.filter((shot) => shot === 'goal').length;

  if (phase === 'pick') {
    return (
      <section className="wc-picker">
        <div className="wc-picker-head">
          <span>Daily Shootout</span>
          <h2>Choose Your Shirt</h2>
        </div>
        <div className="wc-team-grid">
          {teams.map((team) => (
            <button
              key={team.code}
              className="wc-team-pick"
              data-testid={`pick-team-${team.code}`}
              type="button"
              onClick={() => onPick(team)}
            >
              <KitChip team={team} />
              <span className="wc-team-name">{team.name}</span>
            </button>
          ))}
        </div>
      </section>
    );
  }

  return (
    <>
      <div className="wc-shootout-top">
        <div>
          <span className="wc-mini-label">Shooter</span>
          <strong><KitChip team={selectedTeam} showName /></strong>
        </div>
        <div className="wc-dots" aria-label="Shootout shots">
          {Array.from({ length: 5 }, (_, i) => (
            <span key={i} className={shots[i] ?? 'empty'} />
          ))}
        </div>
        <div className="wc-score-readout" data-testid="shootout-score">{goals}/5</div>
      </div>
      {phase === 'play' && (
        <div
          className="wc-aim-surface"
          data-testid="shootout-goal-area"
          onPointerMove={onAim}
          onPointerDown={(event) => {
            onAim(event);
            onShoot();
          }}
        />
      )}
      {feedback && <div className={`wc-shot-flash ${feedback.toLowerCase()}`}>{feedback}</div>}
      {phase === 'ended' && (
        <section className="wc-result-card">
          <span>{goals >= 3 ? 'Win Streak Alive' : 'Keeper Wins'}</span>
          <h2>{goals} of 5</h2>
          <p>Best {record.bestScore}/5 - Games {record.gamesPlayed} - Streak {record.winStreak}</p>
          <div className="wc-result-actions">
            <button type="button" data-testid="shootout-again" onClick={onAgain}>Play again</button>
            <button type="button" onClick={onChangeTeam}>Change team</button>
          </div>
        </section>
      )}
    </>
  );
}

function ModeNav({ mode, onMode }: { mode: CupMode; onMode: (mode: CupMode) => void }) {
  return (
    <nav className="wc-mode-nav" aria-label="World Cup modes">
      <button type="button" data-testid="mode-stadium" className={mode === 'stadium' ? 'active' : ''} onClick={() => onMode('stadium')}>
        Stadium
      </button>
      <button type="button" data-testid="mode-bracket" className={mode === 'bracket' ? 'active' : ''} onClick={() => onMode('bracket')}>
        Bracket
      </button>
      <button type="button" data-testid="mode-shootout" className={mode === 'shootout' ? 'active' : ''} onClick={() => onMode('shootout')}>
        Shootout
      </button>
    </nav>
  );
}

export default function WorldCup({ onBack }: { onBack: () => void }) {
  const [mode, setMode] = useState<CupMode>('stadium');
  const [selectedMatchId, setSelectedMatchId] = useState<string | null>(() => MATCHES[0]?.id ?? null);
  const [shootoutPhase, setShootoutPhase] = useState<ShootoutPhase>('pick');
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [shots, setShots] = useState<ShotResult[]>([]);
  const [feedback, setFeedback] = useState('');
  const [record, setRecord] = useState<ShootoutRecord>(() => readRecord());
  const [stadiumPhysicsReady, setStadiumPhysicsReady] = useState(() => getExistingCupPhysicsRuntime() != null);
  const [physicsHintDismissed, setPhysicsHintDismissed] = useState(false);
  const aimRef = useRef<AimPoint>({ x: 0, y: 0.55 });
  const shotEventRef = useRef<ShootoutEvent | null>(null);
  const shotActiveRef = useRef(false);
  const shotTokenRef = useRef(0);
  const shotsRef = useRef<ShotResult[]>([]);
  const timeoutsRef = useRef<number[]>([]);

  const nextMatch = useMemo(() => findNextMatch(), []);
  const lineup = useMemo(() => pickLineupTeams(nextMatch), [nextMatch]);
  const aliveTeams = useMemo(() => teamsStillAlive(), []);
  const selectedMatch = useMemo(
    () => MATCHES.find((match) => match.id === selectedMatchId) ?? MATCHES[0] ?? null,
    [selectedMatchId],
  );
  const shootoutTeam = selectedTeam ?? aliveTeams[0] ?? fallbackPair()[0];

  useEffect(() => {
    writeCupState({
      mode,
      dataLoaded: true,
      matchCount: MATCHES.length,
      canvasReady: window.__cupState?.canvasReady ?? false,
    });
  }, [mode]);

  useEffect(() => {
    shotsRef.current = shots;
  }, [shots]);

  useEffect(() => () => {
    timeoutsRef.current.forEach((id) => window.clearTimeout(id));
  }, []);

  const resetShootout = useCallback((team = selectedTeam) => {
    setSelectedTeam(team);
    setShots([]);
    shotsRef.current = [];
    setFeedback('');
    shotActiveRef.current = false;
    shotEventRef.current = null;
    aimRef.current = { x: 0, y: 0.55 };
    setShootoutPhase(team ? 'play' : 'pick');
  }, [selectedTeam]);

  const handleAim = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const nx = THREE.MathUtils.clamp(((event.clientX - rect.left) / rect.width) * 2 - 1, -1, 1);
    const ny = THREE.MathUtils.clamp(1 - (event.clientY - rect.top) / rect.height, 0, 1);
    aimRef.current = { x: nx, y: ny };
  }, []);

  const handleShoot = useCallback(() => {
    if (shootoutPhase !== 'play' || shotActiveRef.current || shotsRef.current.length >= 5) return;
    shotActiveRef.current = true;
    const target = { ...aimRef.current };
    const keeperZone = weightedKeeperZone();
    const outcome = decideShot(target, keeperZone);
    const label = outcome === 'goal' ? 'GOAL!' : outcome === 'saved' ? 'SAVED!' : 'POST!';
    setFeedback(label);
    shotTokenRef.current += 1;
    shotEventRef.current = { token: shotTokenRef.current, target, keeperZone, outcome };

    const settleId = window.setTimeout(() => {
      const nextShots = [...shotsRef.current, outcome];
      shotsRef.current = nextShots;
      setShots(nextShots);
      shotActiveRef.current = false;
      if (nextShots.length >= 5) {
        const nextRecord = writeRecord(nextShots);
        setRecord(nextRecord);
        setShootoutPhase('ended');
      }
    }, 650);
    const clearId = window.setTimeout(() => setFeedback(''), 1050);
    timeoutsRef.current.push(settleId, clearId);
  }, [shootoutPhase]);

  const handleStadiumPhysicsReady = useCallback((runtime: CupPhysicsRuntime) => {
    setStadiumPhysicsReady(true);
    writeCupState({ physicsReady: true, fanBallCount: runtime.fanBalls.length });
  }, []);

  const handleStadiumPhysicsUnavailable = useCallback(() => {
    setStadiumPhysicsReady(false);
    writeCupState({ physicsReady: false });
  }, []);

  const handleFanBallCount = useCallback((count: number) => {
    writeCupState({ fanBallCount: count });
  }, []);

  return (
    <div className={`worldcup-page ${feedback === 'GOAL!' ? 'wc-screen-shake' : ''}`}>
      <Canvas
        shadows
        camera={{ position: [0, 16, 32], fov: 48 }}
        gl={{ antialias: true, alpha: false, toneMapping: THREE.ACESFilmicToneMapping, toneMappingExposure: 1.12 }}
      >
        <CanvasStateBridge mode={mode} />
        {mode === 'stadium' && (
          <StadiumScene
            lineup={lineup}
            onPhysicsReady={handleStadiumPhysicsReady}
            onPhysicsUnavailable={handleStadiumPhysicsUnavailable}
            onFanBallCount={handleFanBallCount}
          />
        )}
        {mode === 'bracket' && <BracketScene selectedId={selectedMatchId} onSelect={(match) => setSelectedMatchId(match.id)} />}
        {mode === 'shootout' && <ShootoutScene team={shootoutTeam} aimRef={aimRef} eventRef={shotEventRef} />}
      </Canvas>

      <div className="wc-overlay">
        <header className="wc-topbar">
          <button className="wc-back" type="button" onClick={onBack}>Exit</button>
          <div className="wc-brand">
            <span>The Cup 26</span>
            <strong>FIFA World Cup 2026</strong>
          </div>
        </header>

        {mode === 'stadium' && (
          <>
            <Scoreboard match={nextMatch} />
            <TodayPanel />
            {stadiumPhysicsReady && !physicsHintDismissed && (
              <div className="wc-physics-hint" data-testid="physics-hint">
                <span>click anywhere - physics is real</span>
                <button type="button" aria-label="Dismiss physics hint" onClick={() => setPhysicsHintDismissed(true)}>
                  x
                </button>
              </div>
            )}
          </>
        )}
        {mode === 'bracket' && <BracketDetail match={selectedMatch} />}
        {mode === 'shootout' && (
          <ShootoutHud
            phase={shootoutPhase}
            teams={aliveTeams}
            selectedTeam={selectedTeam}
            shots={shots}
            feedback={feedback}
            record={record}
            onPick={(team) => resetShootout(team)}
            onShoot={handleShoot}
            onAim={handleAim}
            onAgain={() => resetShootout(selectedTeam)}
            onChangeTeam={() => {
              setSelectedTeam(null);
              resetShootout(null);
            }}
          />
        )}
        <ModeNav mode={mode} onMode={setMode} />
      </div>
    </div>
  );
}
